<template>
  <div class="h-full overflow-auto">
    <!-- Header -->
    <div class="mobile-status-padding"></div>
    <div class="grid grid-cols-3 p-6 items-center">
      <div class="ripple cursor-pointer" @click="goBack">Annuler</div>
      <div class="text-2xl font-bold title-color text-center">Filtres</div>
      <div class="text-right ripple cursor-pointer" @click="handleReset">Effacer</div>
    </div>

    <!-- Sort Section -->
    <div class="px-6 font-bold title-color text-xl">Trier par</div>
    <div class="flex flex-col">
      <div
        class="flex items-center gap-2 ripple relative px-6 py-4 cursor-pointer"
        v-for="sort in sortOptions"
        :key="sort.key"
        @click="selectSort(sort)"
      >
        <div class="w-7 h-7">
          <ColorImg :src="sort.icon" class="w-full h-full bg-current" alt="" />
        </div>
        <div>{{ sort.name }}</div>
        <div class="ml-auto">
          <div
            class="w-6 h-6 min-w-6 rounded-lg duration-300 p-1"
            :class="selectedSort?.key === sort.key ? 'bg-primary' : 'bg-slate-200'"
          >
            <ImgComponent
              src="/images/all/check-normal-white.svg"
              class="duration-300 w-full h-full"
              :class="{ 'opacity-0 scale-0': selectedSort?.key !== sort.key }"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Price Range Section -->
    <div class="py-4"></div>
    <div class="px-6 font-bold title-color text-xl">Tranche tarifaire</div>
    <div class="py-1"></div>
    <div class="overflow-auto px-6">
      <div class="min-w-max flex gap-8">
        <div
          v-for="price in priceRanges"
          :key="price.id"
          @click="selectPriceRange(price)"
          :class="{
            'border-transparent': selectedPriceRange?.id !== price.id,
            'border-primary': selectedPriceRange?.id === price.id
          }"
          class="border-2 duration-300 w-20 h-20 bg-slate-100 ripple relative cursor-pointer flex items-center justify-center rounded-full"
        >
          <div>{{ price.label }}</div>
        </div>
      </div>
    </div>

    <!-- Categories Section -->
    <div class="py-4"></div>
    <div class="px-6 font-bold title-color text-xl">Catégories</div>
    <div class="py-2"></div>
    <div class="grid grid-cols-2 gap-6 px-6">
      <div v-for="category in categories" :key="category.id">
        <div
          @click="toggleCategory(category)"
          class="duration-300 border-2 ripple p-4 gap-2 top-0 left-0 w-full h-full flex flex-col items-center justify-center rounded-2xl overflow-hidden bg-slate-100 cursor-pointer relative"
          :class="{
            shimmerElement: category.shimmer,
            'border-transparent': !isCategorySelected(category),
            'border-primary': isCategorySelected(category),
            '!pointer-events-none': category.shimmer
          }"
        >
          <div class="absolute w-full h-full top-0 left-0">
            <ImgComponent
              :src="category.icon || category.iconUrl || category.imageUrl || '/images/categories/default.svg'"
              alt=""
              class="w-full h-full object-cover opacity-20"
            />
          </div>
          <ImgComponent
            :src="category.icon || category.iconUrl || category.imageUrl || '/images/categories/default.svg'"
            alt=""
            class="w-24 h-24 relative rounded-xl object-cover"
          />
          <div class="text-xl relative title-color font-bold">
            {{ category.shimmer ? '.' : category.name }}
          </div>
        </div>
      </div>
    </div>

    <!-- Apply Button -->
    <div class="py-4"></div>
    <div class="p-6 sticky bottom-0" v-if="hasFilterChanges">
      <button class="buttonComponent bg-primary w-full" @click="handleApply">
        Appliquer les filtres
      </button>
      <div class="mobile-padding"></div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import { EVENTS as FILTER_EVENTS, PRICE_RANGES } from '../constants';

// Sort options configuration
const SORT_OPTIONS_CONFIG = [
  {
    key: 'near',
    name: 'Restau à coté',
    icon: '/images/all/restaurant.svg'
  },
  {
    key: 'cookDuration',
    name: 'Temps de préparation',
    icon: '/images/all/history.svg'
  },
  {
    key: 'price',
    name: 'Repas moins Cher',
    icon: '/images/all/discount.svg'
  },
  {
    key: 'popularity',
    name: 'Popularité',
    icon: '/images/all/star.svg'
  }
];

export default {
  name: 'FiltersPage',

  setup() {
    const selectedSort = ref(null);
    const selectedPriceRange = ref(null);
    const selectedCategories = ref([]);
    const categories = ref([]);
    const isLoadingCategories = ref(false);
    const categoriesError = ref(null);

    // Store initial filters for change detection
    const initialSort = ref(null);
    const initialPriceRange = ref(null);
    const initialCategories = ref([]);

    // Store reference to event listener for proper cleanup
    const handleCategoriesResponse = (res) => {
      console.log('[FiltersPage] Network response received:', res);

      if (!res) {
        console.warn('[FiltersPage] Received empty response');
        return;
      }

      if (res.id === 'load-categories') {
        isLoadingCategories.value = false;

        if (res.success) {
          // Store fetched categories
          const fetchedCategories = res.data?.data || res.data || [];
          console.log('[FiltersPage] Categories loaded:', fetchedCategories.length);
          categories.value = fetchedCategories;
        } else {
          const errorMessage = res.data?.message || 'Erreur lors du chargement des catégories';
          categoriesError.value = errorMessage;
          console.error('[FiltersPage] Failed to load categories:', errorMessage);
          // Keep shimmer state on error
        }
      }
    };

    // Load categories from server API
    const loadCategories = () => {
      console.log('[FiltersPage] Loading categories from server');

      // Show shimmer placeholders while loading
      categories.value = Array(12).fill(null).map(() => ({ shimmer: true }));
      isLoadingCategories.value = true;
      categoriesError.value = null;

      // Emit network request
      bus.emit(constants.EVENTS.NETWORK.REQUEST, {
        id: 'load-categories',
        url: '/categories/list?group=ultraxeno',
        method: 'GET'
      });
    };

    // Load current filters from store
    const loadCurrentFilters = () => {
      console.log('[FiltersPage] Loading current filters from store');

      const store = window.$stores?.searchFilters;
      if (!store) {
        console.warn('[FiltersPage] searchFilters store not available');
        return;
      }

      const currentSort = store.getSelectedSort();
      if (currentSort) {
        const sortConfig = SORT_OPTIONS_CONFIG.find(s => s.key === currentSort);
        if (sortConfig) {
          selectedSort.value = sortConfig;
          initialSort.value = sortConfig;
        }
      }

      const currentPriceRange = store.getSelectedPriceRange();
      if (currentPriceRange) {
        const priceConfig = PRICE_RANGES.find(p => p.id === currentPriceRange);
        if (priceConfig) {
          selectedPriceRange.value = priceConfig;
          initialPriceRange.value = priceConfig;
        }
      }

      const currentCategories = store.getSelectedCategories();
      if (currentCategories && currentCategories.length > 0) {
        // Store contains category objects, keep them as is
        selectedCategories.value = [...currentCategories];
        initialCategories.value = [...currentCategories];
      }
    };

    // Select sort option
    const selectSort = (sort) => {
      if (selectedSort.value?.key === sort.key) {
        selectedSort.value = null;
      } else {
        selectedSort.value = sort;
      }
    };

    // Select price range
    const selectPriceRange = (price) => {
      if (selectedPriceRange.value?.id === price.id) {
        selectedPriceRange.value = null;
      } else {
        selectedPriceRange.value = price;
      }
    };

    // Toggle category selection
    const toggleCategory = (category) => {
      console.log('[FiltersPage] Toggling category:', category);

      if (!category || category.shimmer) {
        console.warn('[FiltersPage] Invalid category or shimmer state');
        return;
      }

      const index = selectedCategories.value.findIndex(cat => cat.id === category.id);
      if (index > -1) {
        selectedCategories.value.splice(index, 1);
      } else {
        selectedCategories.value.push(category);
      }
    };

    // Check if category is selected
    const isCategorySelected = (category) => {
      if (!category || !category.id || category.shimmer) {
        return false;
      }
      // selectedCategories contains category objects
      return selectedCategories.value.some(cat => cat.id === category.id);
    };

    // Check if any filter has changed from initial state
    const hasFilterChanges = computed(() => {
      // Check if sort changed
      const sortChanged = selectedSort.value?.key !== initialSort.value?.key;

      // Check if price range changed
      const priceChanged = selectedPriceRange.value?.id !== initialPriceRange.value?.id;

      // Check if categories changed (both are arrays of category objects)
      const categoriesChanged =
        selectedCategories.value.length !== initialCategories.value.length ||
        selectedCategories.value.some(cat =>
          !initialCategories.value.some(initCat => initCat.id === cat.id)
        );

      return sortChanged || priceChanged || categoriesChanged;
    });

    // Reset all filters
    const handleReset = () => {
      console.log('[FiltersPage] Resetting all filters');

      selectedSort.value = null;
      selectedPriceRange.value = null;
      selectedCategories.value = [];

      console.log('[FiltersPage] Emitting RESET_FILTERS event');
      bus.emit(FILTER_EVENTS.SEARCH_FILTERS.RESET_FILTERS);
    };

    // Apply filters and navigate back
    const handleApply = () => {
      console.log('[FiltersPage] Applying filters');

      const store = window.$stores?.searchFilters;
      if (!store) {
        console.error('[FiltersPage] SearchFilters store not found');
        goBack();
        return;
      }

      // Build filters object
      const filters = {
        sort: selectedSort.value?.key || null,
        priceRange: selectedPriceRange.value?.id || null,
        categories: selectedCategories.value.map(c => c.id)
      };

      console.log('[FiltersPage] Applying filters:', filters);

      // Use SET_ALL_FILTERS event to set all filters at once
      bus.emit(FILTER_EVENTS.SEARCH_FILTERS.SET_ALL_FILTERS, filters);

      // Navigate back
      goBack();
    };

    // Navigate back
    const goBack = () => {
      window.history.back();
    };

    // Lifecycle
    onMounted(() => {
      console.log('[FiltersPage] Component mounted');

      // Listen to network responses
      bus.on(constants.EVENTS.NETWORK.RESPONSE, handleCategoriesResponse);
      console.log('[FiltersPage] Event listeners registered');

      loadCategories();
      loadCurrentFilters();
    });

    onUnmounted(() => {
      console.log('[FiltersPage] Component unmounting');

      // Clean up event listener using stored reference
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handleCategoriesResponse);
      console.log('[FiltersPage] Event listeners cleaned up');
    });

    return {
      sortOptions: SORT_OPTIONS_CONFIG,
      priceRanges: PRICE_RANGES,
      categories,
      selectedSort,
      selectedPriceRange,
      selectedCategories,
      selectSort,
      selectPriceRange,
      toggleCategory,
      isCategorySelected,
      hasFilterChanges,
      handleReset,
      handleApply,
      goBack
    };
  }
};
</script>
